import React, { useRef, useState, useEffect } from 'react';
import { Play, Pause, Volume2, VolumeX, Maximize, Minimize } from 'lucide-react';
import { Slider } from './ui/slider';
import { Button } from './ui/button';

const SimpleVideoPlayer = ({ src, poster, className, autoPlay = false }) => {
    const videoRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(autoPlay);
    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isMuted, setIsMuted] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [showControls, setShowControls] = useState(true);
    const controlsTimeoutRef = useRef(null);

    // Toggle Play/Pause
    const togglePlay = (e) => {
        e?.stopPropagation();
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause();
            } else {
                videoRef.current.play();
            }
            setIsPlaying(!isPlaying);
        }
    };

    // Handle Time Update
    const handleTimeUpdate = () => {
        if (videoRef.current) {
            const current = videoRef.current.currentTime;
            const total = videoRef.current.duration;
            if (isFinite(total) && total > 0) {
                setProgress((current / total) * 100);
                setDuration(total);
            }
        }
    };

    // Seek
    const handleSeek = (value) => {
        if (videoRef.current && isFinite(videoRef.current.duration) && videoRef.current.duration > 0) {
            const time = (value[0] / 100) * videoRef.current.duration;
            videoRef.current.currentTime = time;
            setProgress(value[0]);
        }
    };

    // Toggle Mute
    const toggleMute = (e) => {
        e?.stopPropagation();
        if (videoRef.current) {
            videoRef.current.muted = !isMuted;
            setIsMuted(!isMuted);
        }
    };

    // Toggle Fullscreen
    const toggleFullscreen = (e) => {
        e?.stopPropagation();
        const container = videoRef.current?.parentElement;
        if (document.fullscreenElement) {
            document.exitFullscreen();
            setIsFullscreen(false);
        } else if (container) {
            container.requestFullscreen();
            setIsFullscreen(true);
        }
    };

    // Format Time
    const formatTime = (time) => {
        if (!time) return "0:00";
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    };

    // Mouse Move for Controls Visibility
    const handleMouseMove = () => {
        setShowControls(true);
        if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
        if (isPlaying) {
            controlsTimeoutRef.current = setTimeout(() => setShowControls(false), 2000);
        }
    };

    useEffect(() => {
        if (!isPlaying) setShowControls(true);
    }, [isPlaying]);

    useEffect(() => {
        if (autoPlay && videoRef.current) {
            const playPromise = videoRef.current.play();
            if (playPromise !== undefined) {
                playPromise.catch(error => {
                    console.log("Autoplay prevented:", error);
                    setIsPlaying(false);
                });
            }
        }
    }, [autoPlay, src]);

    return (
        <div
            className={`relative group bg-black rounded-lg overflow-hidden flex justify-center items-center ${className}`}
            onMouseMove={handleMouseMove}
            onMouseLeave={() => isPlaying && setShowControls(false)}
        >
            <video
                ref={videoRef}
                src={src}
                poster={poster}
                className="max-w-full max-h-[80vh] w-auto h-auto object-contain cursor-pointer"
                onClick={togglePlay}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={(e) => setDuration(e.target.duration)}
                onEnded={() => setIsPlaying(false)}
                playsInline
                autoPlay={autoPlay}
                disablePictureInPicture
                controlsList="nodownload noplaybackrate"
            />

            {/* Center Play Button Overlay */}
            {!isPlaying && (
                <div
                    className="absolute inset-0 flex items-center justify-center bg-black/30 cursor-pointer pointer-events-none"
                >
                    <div className="bg-white/20 backdrop-blur-sm p-4 rounded-full border border-white/30 text-white shadow-xl group-hover:scale-110 transition-transform duration-200">
                        <Play className="w-8 h-8 fill-current" />
                    </div>
                </div>
            )}

            {/* Custom Control Bar */}
            <div
                className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent px-4 pb-4 pt-8 transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0'}`}
            >
                <div className="flex flex-col gap-2">
                    {/* Progress Bar */}
                    <div className="relative group/slider h-2 flex items-center cursor-pointer">
                        <Slider
                            value={[progress]}
                            max={100}
                            step={0.1}
                            onValueChange={handleSeek}
                            className="z-10"
                        />
                    </div>

                    <div className="flex items-center justify-between mt-1">
                        <div className="flex items-center gap-2">
                            <Button variant="ghost" size="icon" onClick={togglePlay} className="h-8 w-8 text-white hover:bg-white/10 hover:text-blue-400">
                                {isPlaying ? <Pause className="w-5 h-5 fill-current" /> : <Play className="w-5 h-5 fill-current" />}
                            </Button>

                            <div className="flex items-center gap-1 group/volume">
                                <Button variant="ghost" size="icon" onClick={toggleMute} className="h-8 w-8 text-gray-300 hover:text-white">
                                    {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                                </Button>
                            </div>

                            <span className="text-xs font-medium text-gray-300 font-mono">
                                {formatTime(videoRef.current?.currentTime)} / {formatTime(duration)}
                            </span>
                        </div>

                        <div>
                            <Button variant="ghost" size="icon" onClick={toggleFullscreen} className="h-8 w-8 text-gray-300 hover:text-white hover:bg-white/10">
                                {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SimpleVideoPlayer;
